// File: components/navigation/ViewToggle.js

import React from 'react';
import { List, Hash } from 'lucide-react';
import { getSettings, setNavigationView } from '../../utils/storage.js';
import { NAVIGATION_VIEWS } from '../../utils/constants.js';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * ViewToggle Component
 * 
 * Toggle between two navigation modes:
 * - Topic View: Browse by subpart name/description
 * - Code View: Browse by regulation code number
 * 
 * Mobile-optimized: Touch-friendly toggle buttons
 */
const ViewToggle = ({ 
    onViewChange = null,
    className = ''
}) => {
    const { t } = useLanguage();
    const [activeView, setActiveView] = React.useState(NAVIGATION_VIEWS.TOPIC);

    // Load saved navigation view preference on mount
    React.useEffect(() => {
        const settings = getSettings();
        const savedView = settings.navigationView || NAVIGATION_VIEWS.TOPIC;
        setActiveView(savedView);
    }, []);

    // Handle view change
    const handleViewChange = (view) => {
        // Update local state
        setActiveView(view);
        
        // Save to localStorage
        setNavigationView(view);
        
        // Notify parent component
        if (onViewChange) {
            onViewChange(view);
        }

        // Dispatch custom event for other components to listen
        document.dispatchEvent(new CustomEvent('navigation-view-changed', {
            detail: { view }
        }));
    };

    return (
        <div className={`view-toggle ${className}`}>
            {/* Topic View Button */}
            <button
                className={`view-toggle-btn ${activeView === NAVIGATION_VIEWS.TOPIC ? 'active' : ''}`}
                onClick={() => handleViewChange(NAVIGATION_VIEWS.TOPIC)}
                aria-label={t('sidebar.byTopic')}
                aria-pressed={activeView === NAVIGATION_VIEWS.TOPIC}
            >
                <List size={16} />
                <span className="view-toggle-text">{t('sidebar.byTopic')}</span>
            </button>

            {/* Code View Button */}
            <button
                className={`view-toggle-btn ${activeView === NAVIGATION_VIEWS.CODE ? 'active' : ''}`}
                onClick={() => handleViewChange(NAVIGATION_VIEWS.CODE)}
                aria-label={t('sidebar.byCode')}
                aria-pressed={activeView === NAVIGATION_VIEWS.CODE}
            >
                <Hash size={16} />
                <span className="view-toggle-text">{t('sidebar.byCode')}</span>
            </button>
        </div>
    );
};

export default ViewToggle;
